// statistics.ts
type AnimateOptions = { duration?: number };
const DEFAULT_DURATION = 2000;

const isIntegerLike = (str: string): any =>
  /^-?\d{1,3}(?:,\d{3})*$|^-?\d+$/.test(str.trim());

const isDecimalLike = (str: string): any =>
  /^-?(?:\d{1,3}(?:,\d{3})*|\d+)\.\d+$/.test(str.trim());

const parseNumberParts = (raw: string): any => {
  const hasCommas = /,/.test(raw);
  const normalized = raw.replace(/,/g, '');
  const value = parseFloat(normalized);
  const decimals = normalized.includes('.')
    ? normalized.split('.')[1].length
    : 0;
  return { value, decimals, hasCommas };
};

const formatNumber = (n: number, decimals: number, useCommas: boolean): any => {
  return useCommas
    ? n.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : n.toFixed(decimals);
};

const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);

const animateNumber = (
  el: HTMLElement,
  target: number,
  decimals: number,
  useCommas: boolean,
  { duration = DEFAULT_DURATION }: AnimateOptions = {}
): void => {
  const startTime = performance.now();

  const step = (now: number): void => {
    const t = Math.min(1, (now - startTime) / duration);
    const eased = easeOutCubic(t);
    const current = target * eased;

    const output =
      decimals === 0
        ? formatNumber(Math.round(current), 0, useCommas)
        : formatNumber(
            parseFloat(current.toFixed(decimals)),
            decimals,
            useCommas
          );

    el.textContent = output;

    if (t < 1) requestAnimationFrame(step);
  };

  el.textContent = decimals === 0 ? '0' : (0).toFixed(decimals);
  requestAnimationFrame(step);
};

const SCRAMBLE_POOL =
  'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

const animateScrambleText = (
  el: HTMLElement,
  target: string,
  { duration = DEFAULT_DURATION }: AnimateOptions = {}
): void => {
  const chars = target.split('');
  const startTime = performance.now();
  const len = chars.length;
  const resolveTimes = chars.map((_, i) => duration * ((i + 1) / len));

  const step = (now: number) => {
    const elapsed = now - startTime;
    let out = '';

    for (let i = 0; i < len; i++) {
      const c = chars[i];

      if (/\s/.test(c) || (/[^\w]/.test(c) && c !== '_')) {
        out += c;
        continue;
      }
      if (elapsed >= resolveTimes[i]) {
        out += c;
      } else {
        const rand =
          SCRAMBLE_POOL[Math.floor(Math.random() * SCRAMBLE_POOL.length)];
        if (/[A-Z]/.test(c)) out += rand.toUpperCase();
        else if (/[a-z]/.test(c)) out += rand.toLowerCase();
        else out += rand;
      }
    }

    el.textContent = out;
    if (elapsed < duration) requestAnimationFrame(step);
  };

  el.textContent = chars
    .map((c) =>
      /\s/.test(c)
        ? c
        : SCRAMBLE_POOL[Math.floor(Math.random() * SCRAMBLE_POOL.length)]
    )
    .join('');

  requestAnimationFrame(step);
};

const animateElement = (el: HTMLElement, opts?: AnimateOptions): void => {
  if (el.dataset.animatedDone === 'true') return;

  // Allow per-element override via data-duration (ms)
  const durAttr = el.getAttribute('data-duration');
  const duration =
    durAttr && !Number.isNaN(Number(durAttr))
      ? Number(durAttr)
      : DEFAULT_DURATION;

  const targetRaw =
    el.getAttribute('data-animated-string')?.trim() ||
    el.textContent?.trim() ||
    '';

  if (!targetRaw) return;

  if (isIntegerLike(targetRaw)) {
    const { value, hasCommas } = parseNumberParts(targetRaw);
    animateNumber(el, value, 0, hasCommas, { duration });
  } else if (isDecimalLike(targetRaw)) {
    const { value, decimals, hasCommas } = parseNumberParts(targetRaw);
    animateNumber(el, value, decimals, hasCommas, { duration });
  } else {
    animateScrambleText(el, targetRaw, { duration });
  }

  el.dataset.animatedDone = 'true';
};

const statistics = (): void => {
  const selectors = {
    statisticsSection: '[data-statistics]',
    statisticsAnimatedStrings: '[data-animated-string]',
  };

  const supportsReducedMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const sections = document.querySelectorAll<HTMLElement>(
    selectors.statisticsSection
  );

  // Gather all targets under all sections
  const targets: HTMLElement[] = [];
  sections.forEach((section) => {
    section
      .querySelectorAll<HTMLElement>(selectors.statisticsAnimatedStrings)
      .forEach((el) => targets.push(el));
  });

  // If user prefers reduced motion, set final states instantly
  if (supportsReducedMotion) {
    targets.forEach((el) => {
      el.dataset.animatedDone = 'true';
      // Leave original text content as-is (no animation)
    });
    return;
  }

  // Per-element IntersectionObserver
  const io =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (entries, obs) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              const el = entry.target as HTMLElement;
              animateElement(el);
              obs.unobserve(el);
            });
          },
          {
            root: null,
            rootMargin: '0px 0px -10% 0px',
            threshold: 0.2,
          }
        )
      : null;

  if (io) {
    targets.forEach((el) => io.observe(el));
  } else {
    // Fallback: animate immediately if IO not supported
    targets.forEach((el) => animateElement(el));
  }

  // Sizing of Cards
  const equalizeCardHeights = (): void => {
    const uppers: HTMLElement[] = [];
    const middles: HTMLElement[] = [];
    const lowers: HTMLElement[] = [];

    // Collect all uppers, middles, lowers
    document.querySelectorAll('.statistics__card').forEach((card) => {
      const upper = card.querySelector<HTMLElement>('.card__upper');
      const middle = card.querySelector<HTMLElement>('.card__middle');
      const lower = card.querySelector<HTMLElement>('.card__lower');

      if (upper) uppers.push(upper);
      if (middle) middles.push(middle);
      if (lower) lowers.push(lower);
    });

    // Reset heights before measuring (important on resize)
    [uppers, middles, lowers].forEach((group) =>
      group.forEach((el) => (el.style.height = ''))
    );

    // Find tallest in each group
    const upperMax = Math.max(...uppers.map((el) => el.offsetHeight));
    const middleMax = Math.max(...middles.map((el) => el.offsetHeight));
    const lowerMax = Math.max(...lowers.map((el) => el.offsetHeight));

    // Apply equal heights
    uppers.forEach((el) => (el.style.height = `${upperMax}px`));
    middles.forEach((el) => (el.style.height = `${middleMax}px`));
    lowers.forEach((el) => (el.style.height = `${lowerMax}px`));
  };

  // Run on load and resize
  window.addEventListener('load', equalizeCardHeights);
  window.addEventListener('resize', equalizeCardHeights);
};

export default statistics;
